import type { ReactNode } from 'react';
import { useLocation } from 'react-router';

import { Toaster } from 'sonner';

import { communityToasterContainerClassName } from '~/constants/community';

import type { ToastKind } from './types';

const badge = (color: string, d: string): ReactNode => (
  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white ${color}`}>
    <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  </span>
);

const icons: Record<ToastKind, ReactNode> = {
  success: badge('bg-emerald-500', 'M5 13l4 4L19 7'),
  error: badge('bg-red-500', 'M6 18L18 6M6 6l12 12'),
  warning: badge('bg-amber-500', 'M12 9v4m0 4h.01'),
  info: badge('bg-gray-900', 'M12 16v-5m0-3h.01'),
  loading: (
    <svg className="h-5 w-5 shrink-0 animate-spin text-gray-900" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.15" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  ),
};

const classNames = {
  toast:
    '!flex !flex-wrap !items-center !justify-end !gap-x-3 !gap-y-3 !w-[384px] !max-w-[calc(100vw-2rem)] !rounded-xl !border !border-gray-100 !bg-white !p-4 !font-sans !shadow-[0_10px_38px_-10px_rgba(17,24,39,0.25),0_4px_12px_-4px_rgba(17,24,39,0.12)]',
  content: '!flex-1 !min-w-[60%]',
  title: '!whitespace-pre-line !text-sm !font-medium !leading-tight !text-gray-900',
  description: '!mt-1 !text-[13px] !leading-snug !text-gray-500',
  closeButton: '!border-gray-200 !bg-white !text-gray-500 !opacity-100 hover:!text-gray-900',
  actionButton: '!shrink-0 !rounded-md !bg-gray-900 !px-3 !py-1.5 !text-xs !font-medium !text-white hover:!bg-gray-800',
  cancelButton:
    '!shrink-0 !rounded-md !bg-transparent !px-3 !py-1.5 !text-xs !font-medium !text-gray-500 hover:!text-gray-900',
};

// Without `dir`, sonner calls getComputedStyle on <html> every render because Orchid's document sets no `dir`.
export const NotificationToaster = () => {
  const { pathname } = useLocation();

  return (
    <Toaster
      dir="ltr"
      position="top-center"
      gap={10}
      duration={5000}
      expand
      visibleToasts={20}
      closeButton
      className={communityToasterContainerClassName(pathname)}
      toastOptions={{ classNames }}
      icons={icons}
    />
  );
};
